import { CommonModule } from '@angular/common';
import {
  Component,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  WritableSignal,
  inject,
  input,
  signal,
  output
} from '@angular/core';
import { TranslocoModule } from '@ngneat/transloco';
import { noop } from 'rxjs';
import { IconComponent } from '../../icons/icon.component';
import { IconNameType } from '../../icons/icon-name-type.enum';
import { DeferService } from '../../../core/services/ordering/defer.service';
import { CalendarEvent } from '../../../core/models/ordering/calendar-event.model';
import { StoreStatusService } from '../../../core/services/store-status.service';
import { CalendarEventComponent } from './calendar-event/calendar-event.component';
import { DateTime } from 'luxon';
import { GenericObject } from '../../../core/models/core/generic-object';
import { OpenCloseTime } from '../../../core/models/open-close-time.model';

export interface Day {
  date: string;
  open: string | null;
  close: string | null;
  closed: boolean;
}

@Component({
  selector: 'cwo-ordering-calendar',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    IconComponent,
    CalendarEventComponent,
  ],
  templateUrl: './ordering-calendar.component.html',
  styleUrl: './ordering-calendar.component.scss',
})
export class OrderingCalendarComponent implements OnDestroy,OnChanges {
  deferService = inject(DeferService);
  calendarEvents = this.deferService
    .calendarEvents()
    .filter((e) => e.isStoreClosedEvent);

  storeStatusService = inject(StoreStatusService);
  storeStatus = inject(StoreStatusService).storeStatus;
  deferredDayTimes = input<GenericObject<OpenCloseTime | undefined>>();

  deferDateInput = input<DateTime>();

  calendarDays: WritableSignal<Day[][]> = signal([]);
  currentWeek: WritableSignal<Day[]> = signal([]);
  currentWeekIndex: number = 0;
  monthLabel = '';
  focused = false;
  public selectedDate?: Day;
  eventsOfSelectedDate: CalendarEvent[] = [];
  arrowIcon = IconNameType.arrow;
  showClosedStoreLabel = false;

  readonly dateSelected = output<Day>();
  readonly storeClosedForSelectedDate = output<boolean>();

  ngOnChanges(changes: SimpleChanges): void {
    if(changes['deferredDayTimes']){
      this.calculateCalendarDays();
      this.assignFromInput();
      this.displayWeek(this.currentWeekIndex);
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('keydown', noop);
  }

  assignFromInput() {
    if(this.deferDateInput()){
      const targetDate = this.deferDateInput()!;
      const targetWeekIndex = this.calendarDays().findIndex(week =>
        week.some(day => targetDate.hasSame(DateTime.fromISO(day.date), 'day'))
      );
      if(targetWeekIndex !== -1) {
        const day = this.calendarDays()[targetWeekIndex].find(day => targetDate.hasSame(DateTime.fromISO(day.date), 'day'));
        this.selectedDate = day;
        this.showClosedStoreLabel = day!.closed;
        this.currentWeekIndex =targetWeekIndex;
        this.storeClosedForSelectedDate.emit(this.showClosedStoreLabel);
      }
      
    } 
  }

  prevWeek() {
    if (this.hasPreviousWeek()) {
      this.currentWeekIndex--;
      this.displayWeek(this.currentWeekIndex);
    }
  }

  nextWeek() {
    if (this.hasNextWeek()) {
      this.currentWeekIndex++;
      this.displayWeek(this.currentWeekIndex);
    }
  }

  hasPreviousWeek(): boolean {
    return this.currentWeekIndex > 0;
  }

  hasNextWeek(): boolean {
    return this.currentWeekIndex < this.calendarDays().length - 1;
  }

  chooseDate(day: Day) {
    this.showClosedStoreLabel = day.closed;
    if(!day.closed) {
      this.selectedDate = day;
      this.dateSelected.emit(day);
    }
    this.storeClosedForSelectedDate.emit(this.showClosedStoreLabel);
  } 


  calculateCalendarDays() {
    if (this.deferredDayTimes()) {
      
      const dateKeys = Object.keys(this.deferredDayTimes()!).map((date) =>
        DateTime.fromISO(date)
      );
      const firstDate = dateKeys[0];
      const lastDate = dateKeys[dateKeys.length - 1];

      const allDates: Day[] = [];
      let currentDate = firstDate;
      while (currentDate <= lastDate) {
        const dateStr = currentDate.toISODate();
        const dayTimes = this.deferredDayTimes()![dateStr!];
        const isClosed = !dayTimes || !dayTimes.open || !dayTimes.close;

        allDates.push({
          date: dateStr!,
          open: dayTimes?.open || null,
          close: this.deferredDayTimes()![dateStr!]?.close || null,
          closed: isClosed,
        });

        currentDate = currentDate.plus({ days: 1 });
      }

      const weeks: Day[][] = [];
      for (let i = 0; i < allDates.length; i += 7) {
        weeks.push(allDates.slice(i, i + 7));
      }

      this.calendarDays.set(weeks);
    }
  }

  displayWeek(weekIndex: number) {
    this.currentWeek.set(this.calendarDays()![weekIndex]);
    const firstDate = this.currentWeek()?.length ? this.currentWeek()[0] : '';
    if (firstDate) {
      this.monthLabel = DateTime.fromISO(firstDate.date).toFormat('MMMM yyyy');
    }
  }
  getDayOfMonth(dateStr: string): string {
    return DateTime.fromISO(dateStr).toFormat('d');
  }

  getDayName(date: string): string {
    return DateTime.fromISO(date).toFormat('ccc');
  }
  checkIfDateIsAvailable(date: string) {
    let closed = false;
    const dateTimeFormat = DateTime.fromISO(date).startOf('day');

    for (let index = 0; index < this.calendarEvents.length; index++) {
      const event = this.calendarEvents[index];
      const endDate = event.endDateISO
        ? event.endDateISO
        : DateTime.fromISO(event.startDate).endOf('day');

      if (dateTimeFormat >= event?.startDateISO && dateTimeFormat <= endDate) {
        closed = true;
        break;
      }
    }
    return closed;
  }
}
